/**
 * Hiring-demand board — the two things a lead does to a WHOLE unit.
 *
 * Both live on the unit's own row, beside its name. They were each somewhere
 * else and each hard to find: Submit was `opacity-0` until the row was hovered,
 * and Add team was a row at the very bottom of the unit's expanded contents,
 * below every role and the entire current-team list. The two actions a unit lead
 * comes to this board for were the two hardest to reach.
 *
 * Split out of DemandChrome.tsx at the 500-line ceiling; the seam is real, since
 * everything left there is the frame around the rows rather than a control.
 */
import { useState } from 'react';
import { Loader2, Plus, RefreshCw } from 'lucide-react';
import { relativeTime } from './demandUi';

export function UnitSubmit({
  submitted,
  busy,
  onSubmit,
}: {
  submitted?: { submittedBy?: string; submittedAt?: string };
  busy: boolean;
  onSubmit: () => void;
}) {
  /**
   * A SUBMITTED UNIT CAN BE SUBMITTED AGAIN.
   *
   * "I wanted to change my submitted content, but it was not possible." Nothing
   * about this board locks — that was the decision from the start — but the
   * button was replaced by a static chip the moment it was pressed, so the state
   * said "done" and offered no way to say it again after changing something. A
   * submission is a signal, and a signal you cannot re-send is a signal that
   * goes stale. The chip now carries the update.
   */
  if (submitted?.submittedAt) {
    return (
      <span className="flex shrink-0 items-center gap-1">
        <span
          className="shrink-0 rounded-sm bg-emerald-50 px-1.5 text-[11px] font-semibold leading-[22px] text-emerald-700"
          title={`Submitted by ${submitted.submittedBy} ${relativeTime(submitted.submittedAt)} — nothing is locked`}
        >
          submitted {relativeTime(submitted.submittedAt)}
        </span>
        <button
          type="button"
          onClick={onSubmit}
          disabled={busy}
          title="Changed something since? Send the unit again — the board never locked."
          className="inline-flex h-[22px] shrink-0 items-center gap-1 rounded-sm border border-emerald-600 px-2 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-50 disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <RefreshCw className="h-2.5 w-2.5" />}
          Update
        </button>
      </span>
    );
  }

  /*
    ALWAYS VISIBLE, and weighted like the action it is. It used to be
    `opacity-0 … group-hover:opacity-100`, so the one thing a unit lead is here
    to do was invisible until they happened to hover the right row — a button
    nobody can see is a step nobody takes.
  */
  return (
    <button
      type="button"
      onClick={onSubmit}
      disabled={busy}
      className="inline-flex h-[22px] shrink-0 items-center gap-1 rounded-sm bg-emerald-600 px-2 text-[11px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
    >
      {busy && <Loader2 className="h-2.5 w-2.5 animate-spin" />}
      Submit unit
    </button>
  );
}

/**
 * Adding a team, ON THE UNIT'S OWN ROW.
 *
 * It used to be a row of its own at the bottom of the unit's contents, below
 * every role and the current-team list — so the control that changes a unit's
 * shape sat as far as possible from the unit, and could only be reached by
 * expanding it first. It belongs next to the other thing you do to a whole
 * unit.
 *
 * A plain input rather than the design-system `Input`, for the reason already
 * documented for the reply-editor prompt: `Input` is a 56px floating-label form
 * field and hides its placeholder until focus, which is wrong for a one-line
 * control inside a data grid.
 */
export function AddTeam({ unitName, onAdd }: { unitName: string; onAdd: (name: string) => void }) {
  const [value, setValue] = useState('');
  const [editing, setEditing] = useState(false);

  if (editing) {
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const v = value.trim();
          if (v) onAdd(v);
          setValue('');
          setEditing(false);
        }}
      >
        <input
          autoFocus
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={() => setEditing(false)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setEditing(false);
          }}
          placeholder="Team name, then Enter"
          aria-label={`New team under ${unitName}`}
          className="h-[22px] w-52 rounded-sm border border-gray-300 px-1.5 text-[12px] focus:border-blue-500 focus:outline-none"
        />
      </form>
    );
  }
  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className="inline-flex h-[22px] shrink-0 items-center gap-1 rounded-sm border border-gray-300 px-2 text-[11px] font-semibold text-gray-600 hover:border-blue-400 hover:bg-blue-50 hover:text-blue-700"
    >
      <Plus className="h-2.5 w-2.5" />
      Add team
    </button>
  );
}

